import React, { useState } from "react";
import {
  Users,
  Plus,
  Trash2,
  Info,
  Mail,
  Shield,
  ChevronDown,
  ChevronUp,
  Briefcase,
  DollarSign,
  FileText,
  Calendar,
  BarChart3,
  Settings,
  UserCog,
  TriangleAlert,
  SkipForward,
} from "lucide-react";
import SearchableSelect from "../../ui/SearchableSelect";
import Toggle from "../../ui/Toggle";

// ─── Permission Types ────────────────────────────────────────────────────────

export interface UserPermissions {
  canManageBilling: boolean;
  canViewRevenue: boolean;
  canManageClients: boolean;
  canManageMatters: boolean;
  canManageDocuments: boolean;
  canManageCalendar: boolean;
  canManageTeam: boolean;
  canViewReports: boolean;
  canManageSettings: boolean;
}

export const PERMISSION_LABELS: {
  key: keyof UserPermissions;
  label: string;
  description: string;
  icon: React.FC<any>;
}[] = [
  {
    key: "canManageClients",
    label: "Client Management",
    description: "Create, edit, and manage client records",
    icon: Users,
  },
  {
    key: "canManageMatters",
    label: "Matter Management",
    description: "Create, assign, and manage matters",
    icon: Briefcase,
  },
  {
    key: "canManageBilling",
    label: "Billing & Invoicing",
    description: "Create invoices, manage payments, trust accounts",
    icon: DollarSign,
  },
  {
    key: "canViewRevenue",
    label: "Revenue & Financials",
    description: "View firm revenue, profit margins, financial dashboards",
    icon: BarChart3,
  },
  {
    key: "canManageDocuments",
    label: "Documents",
    description: "Upload, organize, and manage documents",
    icon: FileText,
  },
  {
    key: "canManageCalendar",
    label: "Calendar & Tasks",
    description: "Manage calendar events, deadlines, and tasks",
    icon: Calendar,
  },
  {
    key: "canViewReports",
    label: "Reports",
    description: "Generate and view firm reports",
    icon: BarChart3,
  },
  {
    key: "canManageTeam",
    label: "Team Management",
    description: "Invite and manage team members",
    icon: UserCog,
  },
  {
    key: "canManageSettings",
    label: "Firm Settings",
    description: "Modify firm-wide settings and configuration",
    icon: Settings,
  },
];

// ─── Role Defaults ───────────────────────────────────────────────────────────

export const ROLE_DEFAULT_PERMISSIONS: Record<string, UserPermissions> = {
  partner: {
    canManageBilling: true,
    canViewRevenue: true,
    canManageClients: true,
    canManageMatters: true,
    canManageDocuments: true,
    canManageCalendar: true,
    canManageTeam: true,
    canViewReports: true,
    canManageSettings: true,
  },
  associate: {
    canManageBilling: false,
    canViewRevenue: false,
    canManageClients: true,
    canManageMatters: true,
    canManageDocuments: true,
    canManageCalendar: true,
    canManageTeam: false,
    canViewReports: true,
    canManageSettings: false,
  },
  paralegal: {
    canManageBilling: false,
    canViewRevenue: false,
    canManageClients: true,
    canManageMatters: true,
    canManageDocuments: true,
    canManageCalendar: true,
    canManageTeam: false,
    canViewReports: false,
    canManageSettings: false,
  },
  clerk: {
    canManageBilling: false,
    canViewRevenue: false,
    canManageClients: false,
    canManageMatters: true,
    canManageDocuments: true,
    canManageCalendar: true,
    canManageTeam: false,
    canViewReports: false,
    canManageSettings: false,
  },
  assistant: {
    canManageBilling: false,
    canViewRevenue: false,
    canManageClients: true,
    canManageMatters: false,
    canManageDocuments: true,
    canManageCalendar: true,
    canManageTeam: false,
    canViewReports: false,
    canManageSettings: false,
  },
  admin: {
    canManageBilling: true,
    canViewRevenue: true,
    canManageClients: true,
    canManageMatters: true,
    canManageDocuments: true,
    canManageCalendar: true,
    canManageTeam: true,
    canViewReports: true,
    canManageSettings: true,
  },
  bookkeeper: {
    canManageBilling: true,
    canViewRevenue: true,
    canManageClients: true,
    canManageMatters: true,
    canManageDocuments: true,
    canManageCalendar: false,
    canManageTeam: false,
    canViewReports: true,
    canManageSettings: false,
  },
};

// ─── Role Options ────────────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  {
    value: "partner",
    label: "Partner",
    description: "Full access  -  billing, revenue, settings, team management",
  },
  {
    value: "associate",
    label: "Associate",
    description: "Clients, matters, documents, calendar, reports",
  },
  {
    value: "paralegal",
    label: "Paralegal",
    description: "Case support  -  clients, matters, documents, tasks",
  },
  {
    value: "clerk",
    label: "Law Clerk",
    description: "Research & review  -  matters, documents, calendar",
  },
  {
    value: "assistant",
    label: "Legal Assistant",
    description: "Scheduling, communications, client records, documents",
  },
  {
    value: "admin",
    label: "Office Admin",
    description: "Full administrative access  -  billing, team, settings",
  },
  {
    value: "bookkeeper",
    label: "Bookkeeper",
    description: "Financial management  -  billing, clients, matters, revenue, reports",
  },
];

// ─── Types ───────────────────────────────────────────────────────────────────

export interface TeamInvite {
  email: string;
  name: string;
  role: string;
  permissions: UserPermissions;
}

interface InviteTeamStepProps {
  invites: TeamInvite[];
  onChange: (invites: TeamInvite[]) => void;
  currentUserEmail?: string;
  onSkip?: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const inputClasses =
  "w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm";

function getDefaultPermissions(role: string): UserPermissions {
  return (
    ROLE_DEFAULT_PERMISSIONS[role] || { ...ROLE_DEFAULT_PERMISSIONS.associate }
  );
}

function countActivePermissions(perms: UserPermissions): number {
  return Object.values(perms).filter(Boolean).length;
}

function getEmailWarning(
  email: string,
  index: number,
  rows: TeamInvite[],
  currentUserEmail?: string
): string | null {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed) return null;

  // Check if it matches the current user's email
  if (currentUserEmail && trimmed === currentUserEmail.trim().toLowerCase()) {
    return "This is your email  -  you're already a member of this firm.";
  }

  // Check for duplicates across other invite rows
  const duplicateIndex = rows.findIndex(
    (r, i) => i !== index && r.email.trim().toLowerCase() === trimmed
  );
  if (duplicateIndex !== -1) {
    return "This email is already listed above.";
  }

  return null;
}

// ─── Component ───────────────────────────────────────────────────────────────

const InviteTeamStep: React.FC<InviteTeamStepProps> = ({
  invites,
  onChange,
  currentUserEmail,
  onSkip,
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const rows: TeamInvite[] =
    invites.length > 0
      ? invites
      : [
          {
            email: "",
            name: "",
            role: "associate",
            permissions: getDefaultPermissions("associate"),
          },
        ];

  const addInvite = () => {
    onChange([
      ...rows,
      {
        email: "",
        name: "",
        role: "associate",
        permissions: getDefaultPermissions("associate"),
      },
    ]);
  };

  const removeInvite = (index: number) => {
    if (rows.length <= 1) return;
    const updated = rows.filter((_, i) => i !== index);
    onChange(updated);
    if (expandedIndex === index) setExpandedIndex(null);
    else if (expandedIndex !== null && expandedIndex > index)
      setExpandedIndex(expandedIndex - 1);
  };

  const updateField = (
    index: number,
    field: "email" | "name",
    value: string
  ) => {
    const updated = rows.map((invite, i) =>
      i === index ? { ...invite, [field]: value } : invite
    );
    onChange(updated);
  };

  const updateRole = (index: number, role: string) => {
    const updated = rows.map((invite, i) =>
      i === index
        ? { ...invite, role, permissions: getDefaultPermissions(role) }
        : invite
    );
    onChange(updated);
  };

  const togglePermission = (index: number, key: keyof UserPermissions) => {
    const updated = rows.map((invite, i) =>
      i === index
        ? {
            ...invite,
            permissions: {
              ...invite.permissions,
              [key]: !invite.permissions[key],
            },
          }
        : invite
    );
    onChange(updated);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-slate-900">Invite your team</h2>
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-400 leading-none">
            Optional
          </span>
          {onSkip && (
            <button
              type="button"
              onClick={onSkip}
              className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-slate-100 to-slate-50 text-slate-500 border border-slate-200 hover:from-slate-200 hover:to-slate-100 hover:text-slate-700 hover:border-slate-300 shadow-sm hover:shadow transition-all"
            >
              <SkipForward className="h-3.5 w-3.5" />
              Skip this step
            </button>
          )}
        </div>
        <p className="text-slate-500 mt-1 text-sm">
          Add partners, associates, and staff to your Kavoa workspace. You can
          always add more later.
        </p>
      </div>

      {/* Invite List */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <Users className="h-5 w-5 text-brand" />
          <h3 className="text-base font-semibold text-slate-800">
            Team Members
          </h3>
        </div>

        {rows.map((invite, index) => {
          const isExpanded = expandedIndex === index;
          const activeCount = countActivePermissions(invite.permissions);
          const totalCount = PERMISSION_LABELS.length;

          return (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden"
            >
              {/* Main row */}
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-center">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1 md:sr-only">
                      Name
                    </label>
                    <input
                      type="text"
                      className={inputClasses}
                      value={invite.name}
                      onChange={(e) => updateField(index, "name", e.target.value)}
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1 md:sr-only">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                      <input
                        type="email"
                        className={`${inputClasses} pl-10 ${
                          getEmailWarning(invite.email, index, rows, currentUserEmail)
                            ? "border-amber-300 focus:border-amber-500 focus:ring-amber-500/20"
                            : ""
                        }`}
                        value={invite.email}
                        onChange={(e) =>
                          updateField(index, "email", e.target.value)
                        }
                        placeholder="Email address"
                      />
                    </div>
                    {(() => {
                      const warning = getEmailWarning(invite.email, index, rows, currentUserEmail);
                      if (!warning) return null;
                      return (
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <TriangleAlert className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                          <p className="text-xs text-amber-600">{warning}</p>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-2 justify-end">
                    {rows.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => removeInvite(index)}
                        className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        title="Remove team member"
                      >
                        <Trash2 className="h-4.5 w-4.5" />
                      </button>
                    ) : (
                      <div className="w-9 h-9" />
                    )}
                  </div>
                </div>

                {/* Role selector */}
                <div className="mt-3">
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Role
                  </label>
                  <SearchableSelect
                    options={ROLE_OPTIONS}
                    value={invite.role}
                    onChange={(val) => updateRole(index, val || "associate")}
                    placeholder="Select role..."
                    searchPlaceholder="Search roles..."
                    icon={Shield}
                  />
                </div>
              </div>

              {/* Permissions bar  -  full width, tied to user card */}
              <button
                type="button"
                onClick={() =>
                  setExpandedIndex(isExpanded ? null : index)
                }
                className={`w-full flex items-center justify-between px-4 py-2.5 text-xs transition-all border-t ${
                  isExpanded
                    ? "bg-brand-light border-brand-light text-brand"
                    : "bg-slate-50 border-slate-100 text-slate-500 hover:bg-slate-100"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Shield className={`h-3.5 w-3.5 ${isExpanded ? "text-brand-accent" : "text-slate-400"}`} />
                  <span className="font-medium">
                    {invite.name?.trim() || "This user"}'s permissions
                  </span>
                  <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold ${
                    isExpanded
                      ? "bg-brand-light text-brand"
                      : "bg-slate-200 text-slate-600"
                  }`}>
                    {activeCount} of {totalCount}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-xs">
                  <span>{isExpanded ? "Hide" : "Edit"}</span>
                  {isExpanded ? (
                    <ChevronUp className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )}
                </div>
              </button>

              {/* Expandable permissions panel */}
              {isExpanded && (
                <div className="border-t border-slate-100 bg-slate-50/30 px-4 py-4">
                  <p className="text-xs text-slate-400 mb-3 flex items-center gap-1.5">
                    <Settings className="h-3 w-3" />
                    These can be changed anytime in Settings &rarr; Team
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {PERMISSION_LABELS.map(({ key, label, description, icon: PermIcon }) => {
                      const enabled = invite.permissions[key];
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => togglePermission(index, key)}
                          className={`flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${
                            enabled
                              ? "bg-brand-light border-brand-light"
                              : "bg-white border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          {/* Toggle */}
                          <div className="mt-0.5 flex-shrink-0">
                            <Toggle checked={enabled} onChange={() => {}} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <PermIcon
                                className={`h-3.5 w-3.5 flex-shrink-0 ${
                                  enabled
                                    ? "text-brand"
                                    : "text-slate-400"
                                }`}
                              />
                              <span
                                className={`text-sm font-medium ${
                                  enabled
                                    ? "text-brand"
                                    : "text-slate-600"
                                }`}
                              >
                                {label}
                              </span>
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5 leading-tight">
                              {description}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Add button */}
        <button
          type="button"
          onClick={addInvite}
          className="border-2 border-dashed border-slate-300 hover:border-brand-accent hover:text-brand rounded-xl py-3 text-sm font-medium text-slate-500 transition-all w-full flex items-center justify-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add another team member
        </button>
      </div>

      {/* Info callout */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800 flex items-start gap-3">
        <Info className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
        <p>
          Team members will receive an email invitation to join your Kavoa
          workspace. Permissions are editable and can be changed anytime in
          Settings &rarr; Team.
        </p>
      </div>
    </div>
  );
};

export default InviteTeamStep;
